import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import {
  AlertController,
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonicSafeString,
  IonInput,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonList,
  IonRow,
  IonToggle,
  LoadingController,
} from '@ionic/angular/standalone';
import { IonicScrollManagerService, ScreensizeService, markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import {
  DecaCompanyRole,
  getEmptyDecaDocumentSegment,
  getEmptyIDecaDocumentStop,
  IDecaDocument,
  IDecaDocumentCompany,
  IDecaDocumentSegment,
  IDecaDocumentStop,
  isDecaServicePriorToTimestamp,
  MAX_DECA_DOCUMENT_SEGMENTS,
  DecaStopType,
  UUID_NULL,
} from '@okcargo-apps/shared-domain';
import { APP_CONFIG } from '@okcargo-apps/shared-infrastructure';
import {
  dateSinceAfterNowValidator,
  dateUntilNotBeforeSinceValidator,
  detailedEmailValidator,
} from '@okcargo-apps/utils';
import { format } from 'date-fns';
import { AccountService } from '../../account';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaCompanyStoreService } from '../deca-company-store.service';
import { DecaEditCompanyComponent } from '../deca-edit-company/deca-edit-company.component';
import { DecaEditSegmentComponent } from '../deca-edit-segment/deca-edit-segment.component';
import { mapFormToDocument, mapLocationSourceToStopLocationData } from '../deca-forms.mappers';
import {
  DecaCompanyFormModel,
  DecaCompanyMode,
  DecaDocumentFormModel,
  DecaEditReasonFormModel,
  DecaSegmentFormModel,
  DecaStopFormModel,
  IPreviousAddressData,
  ISegmentLocationData,
  IStopLocationData,
} from '../deca-forms.model';
import {
  buildDecaFormWarningMessage,
  getDecaFormWarnings,
  hasDecaCompanyData,
  hasDecaFormWarnings,
  IDecaFormWarnings,
} from '../deca-forms.utils';
import {
  carrierDataValidator,
  chronologicalSegmentDateValidator,
  decaBlockedShipperTaxIdValidator,
  specialAuthorizationTypeRequiredValidator,
} from '../deca-forms.validators';
import { DecaMutationsService } from '../deca-mutations.service';
import { DECA_ROLE } from '../deca-role.utils';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-host',
  templateUrl: './deca-edit-host.component.html',
  styleUrls: ['./deca-edit-host.component.scss'],
  imports: [
    IonItemDivider,
    IonItemGroup,
    IonList,
    IonItem,
    IonGrid,
    IonRow,
    IonCol,
    ReactiveFormsModule,
    IonButton,
    IonIcon,
    IonInput,
    IonToggle,
    DecaEditReasonComponent,
    DecaEditCompanyComponent,
    DecaEditSegmentComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecaEditHostComponent {
  public document = input.required<IDecaDocument>();
  public postSaveRoute = input.required<string>();
  public readonly formResetRequested = output<void>();

  protected readonly maxSegments = MAX_DECA_DOCUMENT_SEGMENTS;

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly decaCompanyStoreService = inject(DecaCompanyStoreService);
  private readonly accountService = inject(AccountService);
  private readonly config = inject(APP_CONFIG);

  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly loadingCtrl = inject(LoadingController);
  private readonly alertCtrl = inject(AlertController);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly screensizeService = inject(ScreensizeService);

  private readonly formVersion = signal(0);

  protected readonly isSmDown = this.screensizeService.isSmDown;

  protected readonly hasSpecialAuthorization = signal(false);

  protected readonly withoutCredits = computed(() => {
    const credits = this.decaStoreService.availableCredits();
    const documentId = this.document().id;

    if (credits < 1 && (!documentId || documentId === UUID_NULL)) return true;
    return false;
  });

  protected readonly buyCreditsUrl =
    'https://documentodecontroldigital.com/planes-y-precios/' +
    `?utm_source=${this.config.utmSource ?? 'deca'}&utm_medium=app` +
    `&utm_campaign=buy-credits&utm_content=no-credits-notice`;

  protected readonly isServiceStarted = computed(() => this.isStartedService(this.document()));

  private isStartedService(doc: IDecaDocument): boolean {
    const isEditingDoc = !!doc.id && doc.id !== UUID_NULL;
    return isEditingDoc && isDecaServicePriorToTimestamp(doc, new Date());
  }

  protected readonly formSignal = linkedSignal({
    source: () => ({
      document: this.document(),
      companyId: this.accountService.account().company.id,
      isDisabled: this.withoutCredits(),
    }),
    computation: (source) => {
      const doc = source.document;
      if (!doc || !source.companyId) return null;

      const form = this.buildForm(doc, source.companyId);
      if (source.isDisabled) {
        form.disable({ emitEvent: false });
      }

      return form;
    },
  });

  protected readonly DECA_ROLE = DECA_ROLE;

  protected readonly shipperMode = signal<DecaCompanyMode>('summary');
  protected readonly carrierMode = signal<DecaCompanyMode>('summary');

  protected readonly segmentLocations = linkedSignal<IDecaDocument | undefined, ISegmentLocationData[]>({
    source: this.document,
    computation: (doc) => {
      if (!doc?.segments?.length) {
        return [{ originLocation: null, destinationLocation: null }];
      }
      return doc.segments.map((seg) => ({
        originLocation: mapLocationSourceToStopLocationData(seg.origin),
        destinationLocation: mapLocationSourceToStopLocationData(seg.destination),
      }));
    },
  });

  protected readonly shipperLocation = linkedSignal<IDecaDocument | undefined, IStopLocationData | null>({
    source: this.document,
    computation: (doc) => (doc ? mapLocationSourceToStopLocationData(doc.shipper) : null),
  });

  protected readonly carrierLocation = linkedSignal<IDecaDocument | undefined, IStopLocationData | null>({
    source: this.document,
    computation: (doc) => (doc ? mapLocationSourceToStopLocationData(doc.carrier) : null),
  });

  protected readonly previousDestinationDataList = computed<Array<IPreviousAddressData | null>>(() => {
    this.formVersion();
    const form = this.formSignal();
    const locations = this.segmentLocations();
    if (!form) return [];

    const segments = form.controls.segments.controls;
    return segments.map((_, index) => {
      if (index <= 0) return null;

      const prevSegGroup = segments[index - 1];
      if (!prevSegGroup) return null;

      const prevDestVal = prevSegGroup.controls.destination.getRawValue();
      const prevLoc = locations[index - 1]?.destinationLocation ?? null;

      return {
        companyName: prevDestVal.companyName,
        street: prevDestVal.street,
        location: prevLoc,
      };
    });
  });

  constructor() {
    // Cross-validation orchestration across all segments (including dynamically added/removed ones)
    effect((onCleanup) => {
      const form = this.formSignal();
      if (!form) {
        this.hasSpecialAuthorization.set(false);
        return;
      }

      this.hasSpecialAuthorization.set(form.controls.hasSpecialAuthorization.value);

      const authSub = form.controls.hasSpecialAuthorization.valueChanges.subscribe((value) => {
        this.hasSpecialAuthorization.set(value);
        form.controls.specialAuthorizationType.updateValueAndValidity({ emitEvent: false });
      });

      const sub = form.controls.segments.valueChanges.subscribe(() => {
        this.formVersion.update((v) => v + 1);
        form.controls.segments.controls.forEach((segGroup) => {
          segGroup.controls.origin.controls.date.updateValueAndValidity({ emitEvent: false });
          segGroup.controls.destination.controls.date.updateValueAndValidity({ emitEvent: false });
        });
      });

      onCleanup(() => {
        authSub.unsubscribe();
        sub.unsubscribe();
      });
    });
  }

  protected addSegment(): void {
    const form = this.formSignal();
    if (!form) return;

    if (form.controls.segments.length >= this.maxSegments) return;

    const newIndex = form.controls.segments.length;
    const newSegForm = this.buildSegmentForm(
      {
        id: crypto.randomUUID(),
        goodsDescription: '',
        netWeight: undefined,
        origin: getEmptyIDecaDocumentStop(),
        destination: getEmptyIDecaDocumentStop(),
      },
      false,
      newIndex,
      form.controls.segments,
    );

    form.controls.segments.push(newSegForm);
    this.segmentLocations.update((list) => [...list, { originLocation: null, destinationLocation: null }]);

    form.controls.segments.updateValueAndValidity();
    this.formVersion.update((v) => v + 1);
  }

  protected removeSegment(index: number): void {
    const form = this.formSignal();
    if (!form) return;

    if (form.controls.segments.length <= 1) return;

    form.controls.segments.removeAt(index);
    this.segmentLocations.update((list) => list.filter((_, i) => i !== index));

    form.controls.segments.updateValueAndValidity();
    this.formVersion.update((v) => v + 1);
  }

  public handleSegmentLocationChange(
    segmentIndex: number,
    event: { type: DecaStopType; location: IStopLocationData | null },
  ): void {
    this.segmentLocations.update((locations) => {
      const updated = [...locations];
      if (!updated[segmentIndex]) {
        updated[segmentIndex] = { originLocation: null, destinationLocation: null };
      }
      if (event.type === 'origin') {
        updated[segmentIndex] = { ...updated[segmentIndex], originLocation: event.location };
      } else {
        updated[segmentIndex] = { ...updated[segmentIndex], destinationLocation: event.location };
      }
      return updated;
    });
    this.formVersion.update((v) => v + 1);
  }

  public async onSubmit(): Promise<void> {
    if (this.connectivity.isOffline()) return;
    if (this.withoutCredits()) return;

    const form = this.formSignal();
    if (!form) return;

    const locations = this.segmentLocations();

    const hasMissingSegmentLocation = locations.some(
      (loc) => !loc.originLocation?.postalCode || !loc.destinationLocation?.postalCode,
    );
    const isShipperLocationMissing = form.controls.shipper && !this.shipperLocation()?.postalCode;
    const hasCarrierEmail = !!form.controls.carrier?.controls.contactEmail.value?.trim();
    const isCarrierLocationMissing = form.controls.carrier && hasCarrierEmail && !this.carrierLocation()?.postalCode;

    if (form.invalid || hasMissingSegmentLocation || isShipperLocationMissing || isCarrierLocationMissing) {
      markFormAsInvalid(form, this.elementRef.nativeElement, this.injector);

      return;
    }

    const warnings = getDecaFormWarnings({
      formValue: form.getRawValue(),
      carrierLocation: this.carrierLocation(),
      isActingShipper: this.document().shipper.id === this.accountService.account().company.id,
      isServiceStarted: this.isServiceStarted(),
    });

    if (hasDecaFormWarnings(warnings) && !(await this.confirmIncompleteDocument(warnings))) return;

    const payload = mapFormToDocument(
      form.getRawValue(),
      this.document(),
      this.segmentLocations(),
      this.shipperLocation(),
      this.carrierLocation(),
    );

    const changeReason = form.controls.changeReason.controls.reason.value.trim();

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const isShipper = this.accountService.account().company.id === payload.shipper.id;
      const isCarrier = this.accountService.account().company.id === payload.carrier.id;

      if (isShipper) {
        await this.decaMutationsService.createOrUpdateDocumentAsShipper(payload, changeReason);
      } else if (isCarrier) {
        await this.decaMutationsService.createOrUpdateDocumentAsCarrier(payload, changeReason);
      } else {
        console.error('The acting company is neither the shipper nor the carrier of the document.');
        return;
      }

      this.cleanForm();
      this.reloadAfterSave();
      this.ionicScrollManagerService.navigateAndScrollToTop(this.postSaveRoute());
    } catch (error) {
      // TODO manage error
      console.error('Error detected in the component when creating or updating the document:', error);
    } finally {
      await loading.dismiss();
    }
  }

  public cleanForm(): void {
    const form = this.formSignal();
    if (!form) return;

    this.segmentLocations.set([{ originLocation: null, destinationLocation: null }]);
    this.shipperLocation.set(null);
    this.carrierLocation.set(null);
    this.shipperMode.set('summary');
    this.carrierMode.set('summary');

    while (form.controls.segments.length > 1) {
      form.controls.segments.removeAt(1);
    }

    form.reset();
    form.markAsPristine();
    form.markAsUntouched();
  }

  public checkDraftInStore(): void {
    const doc = this.document();
    const isCreationMode = !doc?.id || doc.id === UUID_NULL;

    if (isCreationMode && this.hasDraftData()) {
      this.showAlertDraftPrompt();
    }
  }

  public getCurrentDocumentDraft(): IDecaDocument | null {
    const form = this.formSignal();
    if (!form) return null;

    return mapFormToDocument(
      form.getRawValue(),
      this.document(),
      this.segmentLocations(),
      this.shipperLocation(),
      this.carrierLocation(),
    );
  }

  public setCompanyMode(role: DecaCompanyRole, mode: DecaCompanyMode): void {
    if (role === 'shipper') {
      this.shipperMode.set(mode);
    } else {
      this.carrierMode.set(mode);
    }
  }

  public getCompanyFormState(role: DecaCompanyRole): { mode: DecaCompanyMode; hasData: boolean } {
    const mode = role === 'shipper' ? this.shipperMode() : this.carrierMode();
    const companyForm = this.formSignal()?.controls[role];
    const location = role === 'shipper' ? this.shipperLocation() : this.carrierLocation();

    return {
      mode,
      hasData: companyForm ? hasDecaCompanyData(companyForm.getRawValue(), location) : false,
    };
  }

  // - private

  private hasDraftData(): boolean {
    const form = this.formSignal();
    if (!form) return false;

    if (form.dirty) return true;

    const formRawValues = form.getRawValue();
    if (formRawValues.reference?.trim()) return true;
    if (formRawValues.tractorPlateNumber?.trim() || formRawValues.trailerPlateNumber?.trim()) return true;
    if (formRawValues.segments?.length > 1) return true;

    if (formRawValues.shipper) {
      if (
        formRawValues.shipper.companyName?.trim() ||
        formRawValues.shipper.taxId?.trim() ||
        formRawValues.shipper.street?.trim() ||
        formRawValues.shipper.contactEmail?.trim() ||
        this.shipperLocation()?.postalCode
      ) {
        return true;
      }
    }

    if (formRawValues.carrier) {
      if (
        formRawValues.carrier.companyName?.trim() ||
        formRawValues.carrier.taxId?.trim() ||
        formRawValues.carrier.street?.trim() ||
        formRawValues.carrier.contactEmail?.trim() ||
        this.carrierLocation()?.postalCode
      ) {
        return true;
      }
    }

    // TODO check all segments for any non-empty field, not just the first one
    const firstSegment = formRawValues.segments?.[0];
    if (firstSegment) {
      if (firstSegment.goodsDescription?.trim()) return true;
      if (firstSegment.netWeight != null) return true;
      if (firstSegment.origin?.companyName?.trim() || firstSegment.origin?.street?.trim()) return true;
      if (firstSegment.destination?.companyName?.trim() || firstSegment.destination?.street?.trim()) return true;
    }

    return false;
  }

  private async showAlertDraftPrompt(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Borrador sin guardar',
      message: new IonicSafeString(
        'Se han encontrado datos introducidos previamente que no han sido guardados. ¿Desea continuar con estos datos o prefiere reiniciar el formulario?',
      ),
      backdropDismiss: false,
      buttons: [
        {
          text: 'Reiniciar formulario',
          role: 'cancel',
          handler: () => {
            this.cleanForm();
            this.formResetRequested.emit();
          },
        },
        {
          text: 'Continuar con borrador',
          role: 'confirm',
        },
      ],
    });

    await alert.present();
  }

  private async confirmIncompleteDocument(warnings: IDecaFormWarnings): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header: '¡Atención!',
      message: new IonicSafeString(buildDecaFormWarningMessage(warnings)),
      backdropDismiss: false,
      buttons: [
        {
          text: 'Seguir editando',
          role: 'cancel',
        },
        {
          text: 'Guardar',
          role: 'confirm',
        },
      ],
    });

    await alert.present();
    const { role } = await alert.onWillDismiss();

    return role === 'confirm';
  }

  private buildForm(doc: IDecaDocument, currentUserId: string): FormGroup<DecaDocumentFormModel> {
    const hasSpecialAuth = !!doc.specialAuthorizationType;
    const isEditingDoc = !!doc.id && doc.id !== UUID_NULL;
    const isStartedService = this.isStartedService(doc);

    const segmentsArray = this.fb.array<FormGroup<DecaSegmentFormModel>>([]);
    const docSegments = doc.segments?.length ? doc.segments : [getEmptyDecaDocumentSegment()];

    docSegments.forEach((seg, index) => {
      segmentsArray.push(this.buildSegmentForm(seg, isEditingDoc, index, segmentsArray));
    });

    const form = this.fb.group<DecaDocumentFormModel>({
      id: this.fb.control(doc.id),
      reference: this.fb.control(doc.reference ?? ''),
      tractorPlateNumber: this.fb.control(doc.tractorPlateNumber ?? ''),
      trailerPlateNumber: this.fb.control(doc.trailerPlateNumber ?? ''),
      hasSpecialAuthorization: this.fb.control(hasSpecialAuth),
      specialAuthorizationType: this.fb.control(
        doc.specialAuthorizationType ?? '',
        specialAuthorizationTypeRequiredValidator,
      ),
      changeReason: this.fb.group<DecaEditReasonFormModel>({
        reason: this.fb.control('', isStartedService ? Validators.required : null),
      }),
      segments: segmentsArray,
    });

    if (doc.shipper.id !== currentUserId) {
      form.addControl('shipper', this.buildCompanyForm(doc.shipper, false));
    }

    if (doc.carrier.id !== currentUserId) {
      form.addControl('carrier', this.buildCompanyForm(doc.carrier, true));
    }

    form.updateValueAndValidity();
    return form;
  }

  private buildSegmentForm(
    seg: IDecaDocumentSegment,
    isEditingDoc: boolean,
    index: number,
    segmentsArray?: FormArray<FormGroup<DecaSegmentFormModel>>,
  ): FormGroup<DecaSegmentFormModel> {
    const isOriginDateInPast =
      isEditingDoc && seg.origin.date ? new Date(seg.origin.date).getTime() < new Date().getTime() : false;

    const originDateValidators: ValidatorFn[] = isOriginDateInPast ? [] : [dateSinceAfterNowValidator(true)];

    const originForm = this.buildStopForm(seg.origin, originDateValidators);

    if (isOriginDateInPast) {
      originForm.controls.date.disable({ emitEvent: false });
    }

    const destinationForm = this.buildStopForm(seg.destination, [
      dateUntilNotBeforeSinceValidator(() => originForm.controls.date.value, true),
    ]);

    const segmentGroup = this.fb.group<DecaSegmentFormModel>({
      id: this.fb.control(seg.id || crypto.randomUUID()),
      goodsDescription: this.fb.control(seg.goodsDescription ?? '', Validators.required),
      netWeight: this.fb.control(seg.netWeight || null),
      origin: originForm,
      destination: destinationForm,
    });

    if (segmentsArray) {
      originForm.controls.date.addValidators(
        chronologicalSegmentDateValidator(
          () => segmentsArray,
          () => segmentGroup,
        ),
      );
      originForm.controls.date.updateValueAndValidity({ emitEvent: false });
    }

    return segmentGroup;
  }

  private buildCompanyForm(initialData: IDecaDocumentCompany, isCarrier: boolean): FormGroup<DecaCompanyFormModel> {
    return this.fb.group<DecaCompanyFormModel>(
      {
        companyName: this.fb.control(initialData.companyName ?? '', isCarrier ? null : Validators.required),
        taxId: this.fb.control(
          initialData.taxId ?? '',
          isCarrier
            ? [Validators.minLength(8)]
            : [Validators.required, Validators.minLength(8), decaBlockedShipperTaxIdValidator],
        ),
        street: this.fb.control(initialData.street ?? '', isCarrier ? null : Validators.required),
        contactEmail: this.fb.control(initialData.contactEmail ?? '', [detailedEmailValidator(!isCarrier)]),
        contactFirstName: this.fb.control(initialData.contactFirstName ?? '', isCarrier ? null : Validators.required),
        contactLastName: this.fb.control(initialData.contactLastName ?? '', isCarrier ? null : Validators.required),
        contactPhone: this.fb.control(initialData.contactPhone ?? ''),
      },
      {
        validators: isCarrier ? [carrierDataValidator] : [],
      },
    );
  }

  private buildStopForm(
    initialData: IDecaDocumentStop,
    dateValidators: ValidatorFn[] = [],
  ): FormGroup<DecaStopFormModel> {
    const formattedDate = initialData.date ? format(new Date(initialData.date), "yyyy-MM-dd'T'HH:mm") : null;

    return this.fb.group<DecaStopFormModel>({
      companyName: this.fb.control(initialData.companyName ?? '', Validators.required),
      street: this.fb.control(initialData.street ?? '', Validators.required),
      date: this.fb.control<string | null>(formattedDate, dateValidators),
    });
  }

  private reloadAfterSave(): void {
    this.decaStoreService.reloadAfterSaveData();
    this.decaCompanyStoreService.reloadAfterSaveData();
    this.accountService.reloadCompanyDataAfterSave();
  }
}
